// CommentCard.tsx — presentational comment card for the post-detail comments tree.
// Owns only: its Cherish action (via useComments().toggleLike, auth-gated) and the
// edit-history popover open state. All other interactions are delegated via callbacks.

import { useState, useEffect } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import type { Comment } from '@/core/models/comment';
import { useComments, useRequireAuth, useUser } from '@/hooks';
import { Avatar, Button, IconButton, Popover, Textarea } from '@/ui';
import { Icon } from '@/icons';
import { DiscussionBell } from '@/features/discussions/DiscussionBell';
import { EditHistoryPopover } from './EditHistoryPopover';
import {
  CommentTools,
  CommentReferences,
  SelectableCommentBody,
} from '@/features/discussion-tools/DiscussionTools';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface CommentCardProps {
  comment: Comment;
  isOwn: boolean;
  isEditing: boolean;
  onReply: (comment: Comment, authorHandle?: string | null) => void;
  onStartEdit: (id: string) => void;
  onSaveEdit: (id: string, body: string, reason?: string) => void;
  onCancelEdit: () => void;
  onRequestDelete: (id: string) => void;
}

// ─── Styled primitives ────────────────────────────────────────────────────────

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 0;
`;

const AuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AvatarLink = styled(Link)`
  display: inline-flex;
  flex-shrink: 0;
  text-decoration: none;
  border-radius: ${({ theme }) => theme.radius.md};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const MetaCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const NameLine = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const NameLink = styled(Link)`
  font-family: ${({ theme }) => theme.font.display};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 1px;
    border-radius: 2px;
  }
`;

const HandleTime = styled.p`
  display: flex;
  column-gap: 0.9em;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const EditedBtn = styled.button`
  border: none;
  background: transparent;
  padding: 0 2px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  cursor: pointer;
  font-style: italic;

  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const EditedSpan = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  font-style: italic;
  margin-left: 2px;
`;

const AttributionRow = styled.div`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  margin-left: 54px;
`;

const AttributionHandle = styled.span`
  color: ${({ theme }) => theme.color.accentCyan};
`;

const BodyText = styled.p`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  margin: 0 0 0 54px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const EditArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: 54px;
`;

const EditButtons = styled.div`
  display: flex;
  gap: 8px;
`;

// Action row shared base style (mirrors PostActions.tsx baseButtonStyle)
const baseButtonStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 8px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.color.textMuted};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  transition:
    color 0.15s,
    background 0.15s;
  line-height: 1;

  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
    background: ${({ theme }) => theme.color.glassBorder};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 50px;
`;

const ActionBtn = styled.button`
  ${baseButtonStyle}
`;

const LikeBtn = styled(ActionBtn)<{ $active: boolean }>`
  ${({ $active, theme }) =>
    $active &&
    css`
      color: ${theme.color.accentMagenta};
      & svg {
        filter: drop-shadow(0 0 4px ${theme.color.accentMagenta});
      }
    `}
`;

// MenuItem: mirrors PostMoreMenu.tsx
const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;

  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const DangerItem = styled(MenuItem)`
  color: ${({ theme }) => theme.color.danger};
`;

// Tombstone
const Tombstone = styled.div`
  padding: 10px 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  font-style: italic;
`;

const TombstoneNote = styled.p`
  margin: 2px 0 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

// ─── Component ────────────────────────────────────────────────────────────────

export function CommentCard({
  comment,
  isOwn,
  isEditing,
  onReply,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onRequestDelete,
}: CommentCardProps) {
  // Like + auth
  const { toggleLike } = useComments(comment.postId);
  const requireAuth = useRequireAuth();

  // Author
  const author = useUser(comment.authorId);
  const handleWithoutAt = (author?.handle ?? '').replace(/^@/, '');
  const profilePath = `/profile/${handleWithoutAt}`;

  // Edit mode draft
  const [draft, setDraft] = useState(comment.body);
  const [reason, setReason] = useState('');

  // Re-seed draft whenever the card enters edit mode or the underlying body changes,
  // so a cancelled edit never leaves a stale draft on re-entry.
  useEffect(() => {
    if (isEditing) setDraft(comment.body); // eslint-disable-line react-hooks/set-state-in-effect
  }, [isEditing, comment.body]);

  // Edit history popover open state
  const [historyOpen, setHistoryOpen] = useState(false);

  // Own ··· menu open state
  const [menuOpen, setMenuOpen] = useState(false);

  // ─── Tombstone ───────────────────────────────────────────────────────────
  if (comment.deleted) {
    return (
      <Tombstone>
        [deleted]
        <TombstoneNote>This comment has been removed.</TombstoneNote>
      </Tombstone>
    );
  }

  // ─── Handlers ────────────────────────────────────────────────────────────
  function handleLike() {
    if (!requireAuth('cherish')) return;
    void toggleLike(comment.id);
  }

  function handleReply() {
    if (!requireAuth('reply')) return;
    onReply(comment, author?.handle ?? null);
  }

  function handleEdit() {
    setMenuOpen(false);
    // 1.27.70: editing is writing, so it is locked on the Videos pages without two-factor.
    // Deleting your own words stays open everywhere.
    if (!requireAuth('edit comments')) return;
    onStartEdit(comment.id);
  }

  function handleDelete() {
    setMenuOpen(false);
    onRequestDelete(comment.id);
  }

  function handleSave() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (reason.trim()) onSaveEdit(comment.id, trimmed, reason.trim());
    else onSaveEdit(comment.id, trimmed);
  }

  function handleTextareaKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Escape') {
      onCancelEdit();
    }
  }

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <Root id={`comment-${comment.id}`} data-comment-id={comment.id} tabIndex={-1}>
      {/* Author row */}
      <AuthorRow>
        <AvatarLink to={profilePath} aria-label={`${author?.name ?? comment.authorId}'s profile`}>
          <Avatar name={author?.name ?? comment.authorId} size={44} />
        </AvatarLink>

        <MetaCol>
          <NameLine>
            <NameLink to={profilePath}>{author?.name ?? comment.authorId}</NameLink>
            {author?.verified && <Icon name="verify" size={14} title="Verified account" />}
            {/* Edited indicator */}
            {comment.edited && comment.editHistory.length > 0 ? (
              <Popover
                trigger={<EditedBtn type="button">(edited ▾)</EditedBtn>}
                open={historyOpen}
                onOpenChange={setHistoryOpen}
              >
                <EditHistoryPopover comment={comment} />
              </Popover>
            ) : comment.edited ? (
              <EditedSpan>(edited)</EditedSpan>
            ) : null}
          </NameLine>
          <HandleTime data-testid="comment-handle-time">
            {author?.handle && <span>{author.handle}</span>}
            <span>{comment.time}</span>
          </HandleTime>
        </MetaCol>
        {!comment.deleted && <DiscussionBell postId={comment.postId} commentId={comment.id} />}

        {/* Own ··· menu */}
        {isOwn && (
          <Popover
            trigger={<IconButton icon="more-horizontal" label="Comment actions" size={18} />}
            open={menuOpen}
            onOpenChange={setMenuOpen}
          >
            <MenuItem type="button" role="menuitem" onClick={handleEdit}>
              <Icon name="edit" size={16} />
              Edit
            </MenuItem>
            <DangerItem type="button" role="menuitem" onClick={handleDelete}>
              <Icon name="trash" size={16} />
              Delete
            </DangerItem>
          </Popover>
        )}
      </AuthorRow>

      {/* Attribution row */}
      {comment.replyingToHandle && (
        <AttributionRow>
          ↳ Replying to <AttributionHandle>{comment.replyingToHandle}</AttributionHandle>
        </AttributionRow>
      )}

      {/* Body / edit mode */}
      <CommentReferences comment={comment} />
      {isEditing ? (
        <EditArea>
          <Textarea
            label="Edit comment"
            value={draft}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDraft(e.target.value)}
            onKeyDown={handleTextareaKeyDown}
          />
          <EditButtons>
            <Textarea
              label="Reason for edit (optional)"
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
            />
            <Button type="button" disabled={draft.trim().length === 0} onClick={handleSave}>
              Save
            </Button>
            <Button type="button" variant="ghost" onClick={onCancelEdit}>
              Cancel
            </Button>
          </EditButtons>
        </EditArea>
      ) : (
        <SelectableCommentBody comment={comment}>
          <BodyText>{comment.body}</BodyText>
        </SelectableCommentBody>
      )}

      {/* Actions row (hidden while editing) */}
      {!isEditing && <CommentTools comment={comment} isOwn={isOwn} />}
      {!isEditing && (
        <ActionRow>
          <ActionBtn type="button" aria-label="Reply" onClick={handleReply}>
            <Icon name="reply" size={16} />
            Reply
          </ActionBtn>

          <LikeBtn
            type="button"
            aria-label={`${comment.liked ? 'Take back your Cherish' : 'Cherish comment'}, ${comment.likes} cherishes`}
            aria-pressed={comment.liked}
            $active={comment.liked}
            onClick={handleLike}
          >
            <Icon name="cherish" size={16} />
            {comment.likes}
          </LikeBtn>
        </ActionRow>
      )}
    </Root>
  );
}
